import themeStore                from '@/TokamapEditor/stores/themeStore';
import type { ThemeStyle }      from '@/TokamapEditor/stores/themeStore';
import type { Theme }           from '@/TokamapEditor/stores/themeStore';
import type { Shape }           from '@tokamap/core';
import type { ShapeTextStyle }  from '@tokamap/core';
import type { ShapeImageStyle } from '@tokamap/core';
import type { ShapeTheme }      from '@tokamap/core';
import type { ShapeStyle }      from '@tokamap/core';

/** Où se règle, dans le formulaire du thème, ce qu'il impose à une forme. */
export type ThemeFieldTarget = {
	themeId: string;
	section: 'appearance' | 'content';
	scope?: 'normal' | 'hover';
	tab?: 'image' | 'text';
	field: string;
};

export type ThemeLockRef = {
	theme: string;
	target?: ThemeFieldTarget;
};

export type ThemeLock<T> = ThemeLockRef & {
	style: T;
};

type Lock<T> = (key: keyof T) => ThemeLock<T> | undefined;

type ThemePlace = Omit<ThemeFieldTarget, 'themeId' | 'field'>;

const LINKED_KEYS: string[][] = [
	['fixedSizePx', 'size'],
	['rotation', 'rotateWithMap', 'keepReadable'],
	['borderWidth', 'borderWidthUnit'],
];

const FORM_FIELDS: Record<string, string> = {
	fixedSizePx: 'size',
	rotateWithMap: 'rotation',
	keepReadable: 'rotation',
	borderWidthUnit: 'borderWidth',
	horizontalAlign: 'position',
	verticalAlign: 'position',
};

export const MULTIPLE_THEMES = 'plusieurs thèmes';

const themeOf = (shape: Shape) => (shape.themeId ? themeStore.getTheme(shape.themeId) : undefined);

function locker<T extends object>(shapes: Shape[], sourcesOf: (shape: Shape, key: keyof T) => { theme: Theme; sources: [T | undefined, ThemePlace][]; } | undefined): Lock<T> {
	return key => {
		const keys = (LINKED_KEYS.find(group => group.includes(key as string)) ?? [key]) as (keyof T)[];
		const locks = shapes.flatMap(shape => {
			const themed = sourcesOf(shape, key);
			const source = themed?.sources.find(([style]) => keys.some(key => style?.[key] !== undefined));

			return themed && source ? [{ theme: themed.theme, style: source[0]!, place: source[1] }] : [];
		});

		const first = locks[0];

		if (!first) return undefined;

		const single = locks.every(lock => lock.theme.id === first.theme.id);

		return {
			theme: single ? first.theme.name : MULTIPLE_THEMES,
			style: first.style,
			target: single ? { themeId: first.theme.id, ...first.place, field: FORM_FIELDS[key as string] ?? (key as string) } : undefined,
		};
	};
}

/** Le thème qui impose ces réglages de texte à au moins une forme, et les valeurs qu'il impose. */
export function themeTextLock(shapes: Shape[], hover: boolean): Lock<ShapeTextStyle> {
	return locker(shapes, shape => {
		const theme = themeOf(shape);

		if (!theme) return undefined;

		const normal: [ShapeTextStyle, ThemePlace] = [theme.text.normal, { section: 'content', tab: 'text', scope: 'normal' }];

		return { theme, sources: hover ? [[theme.text.hover, { section: 'content', tab: 'text', scope: 'hover' }], normal] : [normal] };
	});
}

/** Mêmes principes que `themeTextLock`, pour l'image. */
export function themeImageLock(shapes: Shape[]): Lock<ShapeImageStyle> {
	return locker(shapes, shape => {
		const theme = themeOf(shape);

		return theme && { theme, sources: [[theme.image, { section: 'content', tab: 'image' }]] };
	});
}

/** Mêmes principes que `themeTextLock`, pour l'apparence : au survol, seul le survol du thème s'impose. */
export function themeStyleLock(shapes: Shape[], hover: boolean): Lock<ThemeStyle> {
	return locker(shapes, shape => {
		const theme = themeOf(shape);

		return theme && { theme, sources: [[hover ? theme.appearance.hover : theme.appearance.normal, { section: 'appearance', scope: hover ? 'hover' : 'normal' }]] };
	});
}

type ThemeValues = Pick<ShapeTheme, 'height' | 'elevation' | 'hoverHeight' | 'hoverElevation' | 'minZoomOffset' | 'volumeOpacity'>;

const HOVER_VALUES: (keyof ThemeValues)[] = ['hoverHeight', 'hoverElevation'];

/** Mêmes principes que `themeTextLock`, pour les réglages du thème qui ne sont pas des styles. */
export function themeValueLock(shapes: Shape[]): Lock<ThemeValues> {
	return locker(shapes, (shape, key) => {
		const theme = themeOf(shape);
		const scope = HOVER_VALUES.includes(key) ? 'hover' : 'normal';

		return theme && { theme, sources: [[themeStore.coreThemes[theme.id], { section: 'appearance', scope }]] };
	});
}

const definedFields = <T extends object>(fields: T | undefined): Partial<T> => Object.fromEntries(Object.entries(fields ?? {}).filter(([, value]) => value !== undefined)) as Partial<T>;

/** Le style que la forme affiche, celui de son thème complété par le sien — même règle que `applyShapeTheme` du cœur. */
export function themedStyle(shape: Shape): ShapeStyle | undefined {
	const theme = shape.themeId ? themeStore.coreThemes[shape.themeId]?.style : undefined;

	if (!theme) return shape.style;

	return {
		...shape.style,
		...definedFields(theme),
		...(theme.borderWidth !== undefined ? { borderWidthUnit: theme.borderWidthUnit } : {}),
	};
}

/** Le contenu et les couleurs du texte que la forme affiche, même règle que `themedStyle`. */
export function themedText(shape: Shape): Pick<ShapeTextStyle, 'text' | 'color' | 'bgColor'> {
	const theme = shape.themeId ? themeStore.coreThemes[shape.themeId]?.text : undefined;

	return {
		text: theme?.text ?? shape.text?.text,
		color: theme?.color ?? shape.text?.color,
		bgColor: theme?.bgColor ?? shape.text?.bgColor,
	};
}

/** L'adresse de l'image que la forme affiche, même règle que `themedStyle`. */
export function themedImageSrc(shape: Shape): string | undefined {
	const theme = shape.themeId && shape.shapeKind !== 'image' ? themeStore.coreThemes[shape.themeId]?.image : undefined;

	return theme?.src ?? shape.image?.src;
}
